import { useState, useEffect } from 'react';
import { Database, TrendingUp, Clock, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { api } from '../api/client';

const TIMEFRAME_ORDER = ['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1', 'W1'];

function staleness(newest) {
  if (!newest) return { label: 'Sin datos', color: 'text-red-400', icon: 'red' };
  const hours = (Date.now() - new Date(newest)) / 3600000;
  if (hours < 1)    return { label: 'Al día',       color: 'text-green-400',  icon: 'green'  };
  if (hours < 24)   return { label: `${Math.round(hours)}h`,  color: 'text-green-400',  icon: 'green'  };
  if (hours < 168)  return { label: `${Math.round(hours/24)}d`,  color: 'text-amber-400',  icon: 'amber'  };
  if (hours < 720)  return { label: `${Math.round(hours/24)}d`,  color: 'text-orange-400', icon: 'orange' };
  return { label: `${Math.round(hours/24)}d`, color: 'text-red-400', icon: 'red' };
}

function StatusDot({ icon }) {
  const colors = {
    green:  'bg-green-400',
    amber:  'bg-amber-400',
    orange: 'bg-orange-400',
    red:    'bg-red-500',
  };
  return <div className={`w-2 h-2 rounded-full shrink-0 ${colors[icon]}`} />;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'America/Mexico_City'
  });
}

function SymbolCard({ symbol, timeframes }) {
  // Ordena timeframes por orden lógico
  const sorted = [...timeframes].sort((a, b) =>
    TIMEFRAME_ORDER.indexOf(a.timeframe) - TIMEFRAME_ORDER.indexOf(b.timeframe)
  );

  const totalCandles = timeframes.reduce((s, t) => s + t.count, 0);
  const hasStale     = timeframes.some(t => staleness(t.newest).icon === 'red' || staleness(t.newest).icon === 'orange');

  return (
    <div className="bg-[#13131A] border border-[#1E1E2E] rounded-lg overflow-hidden">

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#1E1E2E]">
        <div className="flex items-center gap-3">
          <Database size={14} className="text-amber-400" />
          <span className="font-mono font-bold text-slate-100">{symbol}</span>
          {hasStale && (
            <AlertTriangle size={12} className="text-orange-400" />
          )}
        </div>
        <span className="text-[11px] font-mono text-slate-500">
          {totalCandles.toLocaleString()} velas totales
        </span>
      </div>

      {/* Tabla de timeframes */}
      <div className="divide-y divide-[#1E1E2E]">
        {sorted.map(tf => {
          const s = staleness(tf.newest);
          return (
            <div key={tf.timeframe} className="flex items-center gap-4 px-5 py-3">

              {/* Timeframe badge */}
              <span className="w-10 text-[11px] font-mono font-bold text-amber-400">{tf.timeframe}</span>

              {/* Status */}
              <div className="flex items-center gap-2 w-20">
                <StatusDot icon={s.icon} />
                <span className={`text-[11px] font-mono ${s.color}`}>{s.label}</span>
              </div>

              {/* Count */}
              <div className="flex items-center gap-1.5 flex-1">
                <TrendingUp size={11} className="text-slate-600" />
                <span className="text-[11px] font-mono text-slate-400">
                  {tf.count.toLocaleString()} velas
                </span>
              </div>

              {/* Rango */}
              <div className="hidden md:flex items-center gap-1.5 text-[10px] font-mono text-slate-600">
                <Clock size={10} />
                <span>{formatDate(tf.oldest)}</span>
                <span>→</span>
                <span className={s.color}>{formatDate(tf.newest)}</span>
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function GymData() {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.getGymData()
      .then(r => setData(r.data))
      .catch(() => setError('No se pudieron cargar los datos del gym'))
      .finally(() => setLoading(false));
  }, [refresh]);

  const totalCandles = data.reduce((s, sym) =>
    s + sym.timeframes.reduce((ss, t) => ss + t.count, 0), 0);

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="flex items-center gap-3 text-amber-500 font-mono text-sm">
        <div className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
        Cargando datos históricos...
      </div>
    </div>
  );

  return (
    <div className="p-8 space-y-8">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-100 tracking-tight">Datos Históricos</h1>
          <p className="text-xs text-slate-500 font-mono mt-1">
            {data.length} instrumentos · {totalCandles.toLocaleString()} velas totales
          </p>
        </div>
        <button
          onClick={() => setRefresh(r => r + 1)}
          className="flex items-center gap-2 px-3 py-2 rounded border border-[#2A2A3E] text-slate-500 hover:text-amber-400 hover:border-amber-400/30 transition-colors text-[11px] font-mono"
        >
          <RefreshCw size={12} />
          Actualizar
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-400 text-xs font-mono border border-red-400/20 bg-red-400/5 px-3 py-2 rounded">
          <AlertTriangle size={12} />
          {error}
        </div>
      )}

      {/* Leyenda */}
      <div className="flex items-center gap-6 text-[10px] font-mono text-slate-600">
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-green-400" /> Al día (&lt;24h)</div>
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-amber-400" /> &lt;7 días</div>
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-orange-400" /> &lt;30 días</div>
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-red-500" /> Stale (+30d)</div>
      </div>

      {/* Cards por símbolo */}
      <div className="space-y-4">
        {data.map(sym => (
          <SymbolCard key={sym.symbol} {...sym} />
        ))}
      </div>

    </div>
  );
}